import {
  ILiveChatConversation,
  ILiveChatConversationSummary,
  ILiveChatDashboard,
  ILiveChatMessage,
  ILiveChatMessagePage,
  ILiveChatQuickReply,
  LiveChatAgentAvailability,
  LiveChatPriority,
  LiveChatStatus,
  LiveChatTopic,
} from "../models/ILiveChat";
import { configWithUserToken } from "../../services/config";

/* ------------------------------------------------------------------ */
/* Temsilci paneli — /api/livechatagent (RestourantAdmin / SuperAdmin) */
/* ------------------------------------------------------------------ */

export interface ILiveChatAgentUser {
  userId: string;
  customerName: string;
  customerSurName: string;
  customerTelNo: string;
  customerEmail: string;
  roles: string[];
  isEnabled: boolean;
  isOnCall: boolean;
  isOnCallActive: boolean;
}

export const getLiveChatAgents = () =>
  configWithUserToken.get<ILiveChatAgentUser[]>("LiveChatAgent/agents");

export const getLiveChatQueue = () =>
  configWithUserToken.get<ILiveChatConversationSummary[]>("LiveChatAgent/queue");

export const getLiveChatInbox = (filter: {
  status?: LiveChatStatus;
  topic?: LiveChatTopic;
  priority?: LiveChatPriority;
  onlyMine?: boolean;
  search?: string;
  pageIndex?: number;
  pageSize?: number;
}) =>
  configWithUserToken.get<ILiveChatConversationSummary[]>("LiveChatAgent/inbox", {
    params: {
      ...(filter.status ? { status: filter.status } : {}),
      ...(filter.topic ? { topic: filter.topic } : {}),
      ...(filter.priority ? { priority: filter.priority } : {}),
      ...(filter.search ? { search: filter.search } : {}),
      onlyMine: filter.onlyMine ?? false,
      pageIndex: filter.pageIndex ?? 0,
      pageSize: filter.pageSize ?? 20,
    },
  });

export const getAgentConversation = (conversationId: string) =>
  configWithUserToken.get<ILiveChatConversation>(
    `LiveChatAgent/conversations/${conversationId}`
  );

export const getAgentMessages = (
  conversationId: string,
  before?: string | null,
  limit = 30
) =>
  configWithUserToken.get<ILiveChatMessagePage>(
    `LiveChatAgent/conversations/${conversationId}/messages`,
    { params: { ...(before ? { before } : {}), limit } }
  );

export const claimLiveChat = (conversationId: string, agentId?: string) =>
  configWithUserToken.post<ILiveChatConversation>("LiveChatAgent/claim", {
    conversationId,
    agentId: agentId ?? null,
  });

export const transferLiveChat = (body: {
  conversationId: string;
  targetAgentId: string;
  note?: string;
}) => configWithUserToken.post("LiveChatAgent/transfer", body);

export const resolveLiveChat = (body: {
  conversationId: string;
  summary?: string;
  requestRating?: boolean;
}) =>
  configWithUserToken.post("LiveChatAgent/resolve", {
    conversationId: body.conversationId,
    summary: body.summary ?? null,
    requestRating: body.requestRating ?? true,
  });

export const closeLiveChatAsAgent = (conversationId: string, reason?: string) =>
  configWithUserToken.post("LiveChatAgent/close", {
    conversationId,
    reason: reason ?? null,
  });

export const markAgentConversationRead = (conversationId: string) =>
  configWithUserToken.post<number>(`LiveChatAgent/conversations/${conversationId}/read`);

export const updateLiveChatPriority = (conversationId: string, priority: LiveChatPriority) =>
  configWithUserToken.post("LiveChatAgent/priority", { conversationId, priority });

export const setAgentAvailability = (
  availability: LiveChatAgentAvailability,
  maxConcurrentChats?: number
) =>
  configWithUserToken.put("LiveChatAgent/availability", {
    availability,
    ...(maxConcurrentChats != null ? { maxConcurrentChats } : {}),
  });

export const getLiveChatDashboard = () =>
  configWithUserToken.get<ILiveChatDashboard>("LiveChatAgent/dashboard");

export const getAgentQuickReplies = (topic?: LiveChatTopic) =>
  configWithUserToken.get<ILiveChatQuickReply[]>("LiveChatAgent/quick-replies", {
    params: topic ? { topic } : undefined,
  });

export const consumeQuickReply = (quickReplyId: string) =>
  configWithUserToken.post<ILiveChatQuickReply>(
    `LiveChatAgent/quick-replies/${quickReplyId}/use`
  );

export const sendLiveChatMessage = (body: {
  conversationId: string;
  content: string;
  clientMessageId?: string;
}) => configWithUserToken.post<ILiveChatMessage>("LiveChat/messages", body);
